import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControl,
  IconButton,
  Link,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import buildRoute from "components/buildRoute";
import { trackEvent } from "../../../../configureMixpanel";
import PageHeader from "components/PageHeader";
import * as api from "../../fileUploaderAdminApi";

const DATA_TYPES = ["string", "integer", "float", "boolean", "datetime", "date"];

const newColumn = (position) => ({
  id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
  name: "",
  position,
  data_type: "string",
  is_required: false,
  allow_missing: true,
  _isNew: true,
});

const EditSchemaPage = () => {
  const { projectId, schemaId } = useParams();
  const navigate = useNavigate();

  const [schemaName, setSchemaName] = useState("");
  const [columns, setColumns] = useState([]);
  const [templatesUsing, setTemplatesUsing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    const init = async () => {
      try {
        const schemasData = await api.fetchSchemas(projectId);
        const schema = schemasData.find((s) => s.id === schemaId);
        if (!schema) throw new Error("Schema not found.");
        setSchemaName(schema.name);
        setColumns(schema.columns.map((c) => ({ ...c })));
        setTemplatesUsing(schema.templates_using ?? []);
      } catch (err) {
        setSaveError(err.message || "Failed to load schema.");
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [schemaId]);

  const updateColumn = useCallback((id, field, value) => {
    setColumns((cols) => cols.map((c) => (c.id === id ? { ...c, [field]: value } : c)));
  }, []);

  const addColumn = () => setColumns((cols) => [...cols, newColumn(cols.length)]);

  const removeColumn = (id) =>
    setColumns((cols) =>
      cols.filter((c) => c.id !== id).map((c, idx) => ({ ...c, position: idx }))
    );

  const moveColumn = (id, direction) => {
    setColumns((cols) => {
      const idx = cols.findIndex((c) => c.id === id);
      const swapIdx = idx + direction;
      if (swapIdx < 0 || swapIdx >= cols.length) return cols;
      const next = [...cols];
      [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
      return next.map((c, i) => ({ ...c, position: i }));
    });
  };

  const validate = () => {
    const errs = {};
    if (!schemaName.trim()) errs.schemaName = "Schema name is required.";
    const names = columns.map((c) => c.name.trim());
    if (names.some((n) => !n)) errs.columns = "All columns must have a name.";
    const dupes = names.filter((n, i) => n && names.indexOf(n) !== i);
    if (dupes.length) errs.columns = `Duplicate column name(s): ${[...new Set(dupes)].join(", ")}`;
    return errs;
  };

  const handleSave = async () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    setSaveError("");
    try {
      await api.updateSchema(schemaId, {
        name: schemaName.trim(),
        columns: columns.map(({ _isNew, ...col }) => col),
      });
      trackEvent("file_schema_saved", {
        success: true,
        column_count: columns.length,
        templates_affected_count: templatesUsing.length,
      });
      navigate(`/projects/${projectId}/file-uploader/admin?tab=1`);
    } catch (err) {
      setSaveError(err.message || "Failed to save schema.");
      trackEvent("file_schema_saved", { success: false });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link
          component={RouterLink}
          to={`/projects/${projectId}/file-uploader/admin?tab=1`}
          underline="hover"
          color="inherit"
          variant="body2"
        >
          Schemas
        </Link>
        <Typography variant="body2" color="text.primary">{schemaName || "Edit Schema"}</Typography>
      </Breadcrumbs>

      <PageHeader
        title="Edit Schema"
        subtitle={
          templatesUsing.length > 0
            ? `Used by: ${templatesUsing.map((t) => t.name).join(", ")} — changes will affect all of them.`
            : "No templates are currently using this schema."
        }
      />

      {saveError && <Alert severity="error" sx={{ mb: 3 }}>{saveError}</Alert>}

      {templatesUsing.length > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          This schema is used by{" "}
          {templatesUsing.map((t, i) => (
            <span key={t.id}>
              <Link
                component={RouterLink}
                to={`/projects/${projectId}/file-uploader/admin/templates/${t.id}/edit`}
                underline="hover"
              >
                {t.name}
              </Link>
              {i < templatesUsing.length - 1 ? ", " : ""}
            </span>
          ))}
          . Edits will affect all of them.
        </Alert>
      )}

      <Stack spacing={3} sx={{ maxWidth: 860 }}>
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={3}>
              <TextField
                label="Schema Name"
                value={schemaName}
                onChange={(e) => setSchemaName(e.target.value)}
                required
                fullWidth
                error={!!errors.schemaName}
                helperText={errors.schemaName}
              />

              <Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                  <Typography variant="subtitle2">Columns</Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={addColumn}>
                    Add Column
                  </Button>
                </Box>

                {errors.columns && (
                  <Alert severity="error" sx={{ mb: 1 }}>{errors.columns}</Alert>
                )}

                <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "grey.50" }}>
                        <TableCell sx={{ width: 64, textAlign: "center" }}>Order</TableCell>
                        <TableCell>Column Name</TableCell>
                        <TableCell sx={{ width: 140 }}>Type</TableCell>
                        <TableCell sx={{ width: 80, textAlign: "center" }}>Required</TableCell>
                        <TableCell sx={{ width: 80, textAlign: "center" }}>Allow Missing</TableCell>
                        <TableCell sx={{ width: 48 }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {columns.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ color: "text.secondary", py: 3 }}>
                            No columns yet. Click "Add Column" to start.
                          </TableCell>
                        </TableRow>
                      )}
                      {columns.map((col, idx) => (
                        <TableRow key={col.id} sx={{ "&:last-child td": { border: 0 } }}>
                          <TableCell sx={{ textAlign: "center", p: 0.5 }}>
                            <Stack direction="column" alignItems="center" spacing={0}>
                              <IconButton size="small" onClick={() => moveColumn(col.id, -1)} disabled={idx === 0}>
                                <KeyboardArrowUpIcon fontSize="inherit" />
                              </IconButton>
                              <Typography variant="caption" color="text.secondary" lineHeight={1}>
                                {idx + 1}
                              </Typography>
                              <IconButton size="small" onClick={() => moveColumn(col.id, 1)} disabled={idx === columns.length - 1}>
                                <KeyboardArrowDownIcon fontSize="inherit" />
                              </IconButton>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              value={col.name}
                              onChange={(e) => updateColumn(col.id, "name", e.target.value)}
                              placeholder="column_name"
                              fullWidth
                              inputProps={{ style: { fontFamily: "monospace" } }}
                            />
                          </TableCell>
                          <TableCell>
                            <FormControl size="small" fullWidth>
                              <Select
                                value={col.data_type}
                                onChange={(e) => updateColumn(col.id, "data_type", e.target.value)}
                              >
                                {DATA_TYPES.map((t) => (
                                  <MenuItem key={t} value={t}>{t}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell align="center">
                            <Checkbox
                              size="small"
                              checked={col.is_required}
                              onChange={(e) => updateColumn(col.id, "is_required", e.target.checked)}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Checkbox
                              size="small"
                              checked={col.allow_missing}
                              onChange={(e) => updateColumn(col.id, "allow_missing", e.target.checked)}
                            />
                          </TableCell>
                          <TableCell align="center" sx={{ p: 0.5 }}>
                            <Tooltip title="Remove column">
                              <IconButton size="small" color="error" onClick={() => removeColumn(col.id)}>
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                  Tip: "Required" means the column must exist in the file. "Allow Missing" controls whether empty cells are valid.
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Box sx={{ display: "flex", gap: 2, pb: 6 }}>
          <Button
            onClick={() => navigate(`/projects/${projectId}/file-uploader/admin?tab=1`)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={22} /> : "Save Schema"}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default buildRoute({ element: <EditSchemaPage /> });
